import { computed, onBeforeUnmount, useHost, type ComputedRef } from "vue";
import { useSharedState } from "composables/useSharedState";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { createEventDispatcher } from "@/runtime/shared/events";
import type { TabProps } from "./tab.types";
import { tabClasses } from "./tab.styles";

/* -------------------------------------------------------------------------- */
/*  Registry                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Maps each `<e-tab>` host element to a computed ref of its active value.
 * Used by `useTabTrigger` and `useTabContent` to read active state without
 * going through the global state store.
 */
const tabRegistry = new WeakMap<Element, ComputedRef<string | undefined>>();

export const registerTab = (element: Element, activeValue: ComputedRef<string | undefined>): void => {
  tabRegistry.set(element, activeValue);
};

export const unregisterTab = (element: Element): void => {
  tabRegistry.delete(element);
};

/**
 * Returns the active-value computed ref for the given `<e-tab>` element.
 * Returns `undefined` if the element is not registered (e.g. not yet mounted).
 */
export const getTabActiveValue = (element: Element): ComputedRef<string | undefined> | undefined => {
  return tabRegistry.get(element);
};

/* -------------------------------------------------------------------------- */
/*  useTab                                                                     */
/* -------------------------------------------------------------------------- */

export interface UseTabOptions {
  props: Readonly<TabProps>;
}

export const useTab = (options: UseTabOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);

  /**
   * Effective active value: controlled `value` takes precedence,
   * falls back to `defaultValue` for uncontrolled usage.
   */
  const activeValue = computed<string | undefined>(() => state.value?.value ?? state.defaultValue?.value);

  if (host) {
    registerTab(host, activeValue);
  }

  onBeforeUnmount(() => {
    if (host) unregisterTab(host);
  });

  /**
   * Activate a tab by value. Called by `useTabTrigger` via direct element
   * dispatch — exposed here for completeness and potential programmatic use.
   */
  const setActiveValue = (value: string): void => {
    dispatchEvent("update:value", value);
    dispatchEvent("change", value);
  };

  const classes = computed(() => mergeClassProp(tabClasses(), props.class));

  return { classes, activeValue, setActiveValue };
};
